import {
  InputAdornment,
  MenuItem,
  Select,
  Typography,
  TypographyVariant,
  useTheme,
} from '@material-ui/core'
import { useEffect, useState } from 'react'
import { QuantityDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import {
  DefinitiveDecimalTextField,
  DefinitiveDecimalTextFieldProps,
} from './DefinitiveDecimalTextField'

type DefinitiveQuantityFieldProps = {
  quantityDto: QuantityDto
  onChange: (value: QuantityDto) => void
  validation?: (value: QuantityDto) => string[]
  showRawValueInTooltip?: (value: QuantityDto) => string
  typographyVariant?: TypographyVariant
  maxValue?: number
  disabledUnit?: boolean
  disabledValue?: boolean
} & Omit<DefinitiveDecimalTextFieldProps, 'value' | 'onChange'>

export const DefinitiveQuantityField = (
  props: DefinitiveQuantityFieldProps
) => {
  const [currentQuantity, setCurrentQuantity] = useState(props.quantityDto)
  const [currentValidations, setCurrentValidations] =
    useState<string[]>(undefined)

  const theme = useTheme()

  useEffect(() => {
    setCurrentQuantity(props.quantityDto)
  }, [props.quantityDto])

  const handleChange = (field: keyof QuantityDto, value: number | string) => {
    const newQuantity = {
      ...currentQuantity,
      [field]: value,
    }

    setCurrentQuantity(newQuantity)

    const validations = props.validation
      ? props.validation(newQuantity)
      : undefined

    if (validations?.length > 0) {
      setCurrentValidations(validations)
    } else {
      setCurrentValidations(undefined)
      props.onChange(newQuantity)
    }
  }

  if (!props.quantityDto || !currentQuantity) {
    return null
  }

  return (
    <DefinitiveDecimalTextField
      {...props}
      numberFormatProps={{
        ...props.numberFormatProps,
        decimalScale: props.numberFormatProps?.decimalScale ?? 2,
        fixedDecimalScale: true,
        allowNegative: props.numberFormatProps?.allowNegative ?? false,
      }}
      textFieldProps={{
        ...(props.textFieldProps || {}),
        disabled: props.textFieldProps?.disabled ?? props.disabledValue,
        inputProps: {
          ...props.textFieldProps?.inputProps,
          style: {
            ...props.textFieldProps?.inputProps?.style,
            textAlign:
              props.textFieldProps?.inputProps?.style?.textAlign || 'right',
            fontSize:
              theme.typography[props.typographyVariant || 'caption'].fontSize,
            padding:
              props.textFieldProps?.inputProps?.style?.padding || '.1em .2em',
          },
        },
        InputProps: {
          ...props.textFieldProps?.InputProps,
          endAdornment: props.quantityDto?.selectableAbbreviations && (
            <InputAdornment position="end">
              {props.disabledUnit ||
              currentQuantity?.selectableAbbreviations?.length < 2 ? (
                <Typography
                  variant={props.typographyVariant || 'caption'}
                  color="textSecondary"
                >
                  {currentQuantity?.unit}
                </Typography>
              ) : (
                <Select
                  value={currentQuantity?.unit}
                  onChange={(e) => {
                    handleChange('unit', e.target.value as string)
                  }}
                  disabled={props.disabledUnit}
                  error={currentValidations?.length > 0}
                  color={
                    currentValidations?.length > 0 ? 'secondary' : 'primary'
                  }
                  style={{
                    fontSize:
                      theme.typography[props.typographyVariant || 'caption']
                        .fontSize,
                  }}
                  disableUnderline
                  readOnly={props.disabledUnit}
                >
                  {props.quantityDto.selectableAbbreviations.map(
                    (abbreviation) => (
                      <MenuItem key={abbreviation} value={abbreviation}>
                        {abbreviation}
                      </MenuItem>
                    )
                  )}
                </Select>
              )}
            </InputAdornment>
          ),
        },
      }}
      maxValue={props.maxValue}
      value={currentQuantity?.value}
      onChange={(value) => handleChange('value', value)}
      warnings={currentValidations}
      showRawValueInTooltip={props.showRawValueInTooltip}
    />
  )
}
